import { z } from "zod";

/**
 * The Curator's run summary. Lives in `jobs.workerResult.artifacts[0]`
 * once the orchestrator persists the callback (see
 * `services/orchestrator/src/routes/callbacks.ts`).
 *
 * Counters are service-tracked (incremented by the Curator service as it
 * processes each tool call) — never self-reported by the LLM, which would
 * drift. The only free-form contribution from the model is `observations`,
 * populated via the `record_observation` tool when the model wants to note
 * something the structured counters can't express ("Capterra returned 503
 * three times this run").
 *
 * `agentConfigSnapshot` is forensic provenance — it freezes the templating
 * values that drove this run so a row rejected by the quality gate is
 * interpretable later when the threshold may have moved. It coexists
 * deliberately with `jobs.configSnapshot` (the orchestrator's
 * dispatch-time raw freeze of `agent_config!A:B`): this artifact-side
 * field is the curator's *post-Zod* parse — only the keys the agent's
 * boot Zod recognises, with type coercion and defaults gap-fill applied.
 * The dispatch-time freeze covers failed-before-callback jobs where no
 * artifact ever lands. The admin-ui "Config used" panel prefers this
 * artifact snapshot when present and falls back to `configSnapshot`.
 *
 * `promptVersion` is the git SHA of `packages/prompts/src/db_curator/autonomous.md`
 * at run time, or `"dev"` when running from an uncommitted tree. Lets us
 * answer "which prompt produced this row" later.
 *
 * The per-run authorship trail (which draft IDs this run authored) is
 * NOT carried here — it lives in the `prospect.draft_authorship`
 * Postgres table, written per append by the curator service so
 * `/api/catalogue/drafts` can attribute in-flight drafts without
 * waiting on this artifact. See M1 #84 (round 2) and
 * `services/curator/src/draft-authorship.ts`.
 */
export const curatorArtifactsSchema = z.object({
  startedAt: z.string().datetime(),
  finishedAt: z.string().datetime(),
  /** `null` = ran to completion. Any other value names what halted the run. */
  haltReason: z.string().nullable(),

  modesAttempted: z.array(z.enum(["mode_1", "mode_2", "mode_3"])),
  modesCompleted: z.array(z.enum(["mode_1", "mode_2", "mode_3"])),
  /**
   * Modes for which the LLM emitted `record_mode_progress(mode, skipped)`
   * — the legitimate "examined and consciously not progressed" terminal
   * status. Disjoint from `modesCompleted` (which stays `completed`-only).
   * Both together are what the autonomous-mode loop guard reads to
   * distinguish a legitimate skip from a flake exit-after-`started`
   * (see [issue #638](https://github.com/PROSPECT-TECH-LTD/prospect-platform/issues/638)
   * and `docs/curator.md § Autonomous-mode loop guard`). Defaulted to
   * empty for backward compatibility with pre-#638 artifacts on
   * jobs.workerResult that never emitted this field.
   */
  modesSkipped: z.array(z.enum(["mode_1", "mode_2", "mode_3"])).default([]),

  liveRecordsBefore: z.number().int().nonnegative(),
  newDraftsAdded: z.number().int().nonnegative(),
  staleRefreshed: z.object({
    withChanges: z.number().int().nonnegative(),
    withoutChanges: z.number().int().nonnegative(),
  }),
  draftsAwaitingReview: z.number().int().nonnegative(),

  qualityGateRejections: z.array(
    z.object({
      name: z.string().min(1),
      failedChecks: z.array(z.string().min(1)),
    }),
  ),

  observations: z.array(z.string()),

  agentConfigSnapshot: z.record(z.union([z.string(), z.number()])),
  promptVersion: z.string().min(1),
});

export type CuratorArtifacts = z.infer<typeof curatorArtifactsSchema>;
